import React from 'react';
import { Store, Truck } from 'lucide-react';
import { DeliveryMethod } from '../../types';
import { ShippingQuote } from '../../services/shipping';

interface DeliveryChoiceProps {
    value: DeliveryMethod;
    onChange: (method: DeliveryMethod) => void;
    sellerFirstName: string;
    quote: ShippingQuote;
    /** När hemleveransen skickas, t.ex. "Skickas när säljperioden är slut (18 november)". */
    deliveryWhen: string;
    /** Falskt när gruppen bara säljer med hemleverans (inget sätt att betala vid hämtning). */
    pickupEnabled?: boolean;
}

const kr = (n: number) => `${n.toLocaleString('sv-SE')} kr`;

/**
 * Första valet i kassan: hämta hos säljaren eller hemleverans. Frakten syns
 * direkt i valet, så kunden vet vad hemleverans kostar innan hen väljer.
 */
export const DeliveryChoice: React.FC<DeliveryChoiceProps> = ({ value, onChange, sellerFirstName, quote, deliveryWhen, pickupEnabled = true }) => {
    const option = (method: DeliveryMethod, icon: React.ReactNode, title: string, sub: string, price: string, enabled = true) => {
        const selected = value === method;
        return (
            <label
                className={`flex items-center gap-3 p-3.5 rounded-xl border transition-colors ${enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'} focus-within:ring-2 focus-within:ring-coffee-500/30 ${selected ? 'bg-coffee-50 border-coffee-300 ring-1 ring-coffee-300' : 'bg-white border-stone-200 hover:border-coffee-200'}`}
            >
                <input
                    type="radio"
                    name="delivery-method"
                    value={method}
                    checked={selected}
                    disabled={!enabled}
                    onChange={() => onChange(method)}
                    className="sr-only"
                />
                <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? 'bg-coffee-900 text-white' : 'bg-stone-100 text-stone-500'}`}>
                    {icon}
                </span>
                <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-stone-800">{title}</span>
                    <span className="block text-[11px] text-stone-500 leading-snug mt-0.5">{sub}</span>
                </span>
                <span className={`text-sm font-black whitespace-nowrap ${selected ? 'text-coffee-900' : 'text-stone-500'}`}>{price}</span>
            </label>
        );
    };

    return (
        <fieldset className="mb-6">
            <legend className="text-xs font-black uppercase tracking-widest text-stone-400 mb-3 px-1">Hur vill du få ditt kaffe?</legend>
            <div className="space-y-2">
                {pickupEnabled && option('PICKUP', <Store size={16} />, `Hämta hos ${sellerFirstName}`, 'När gruppen fått sin leverans', '0 kr')}
                {option(
                    'HOME',
                    <Truck size={16} />,
                    'Hemleverans',
                    quote.ok ? `Betala nu med Swish eller kort · ${deliveryWhen}` : (quote as { reason: string }).reason,
                    quote.ok ? kr((quote as { price: number }).price) : "–",
                    quote.ok
                )}
            </div>
        </fieldset>
    );
};
